import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { QuestionCategory } from '../api/types';
import { IconArrowRight } from '../icons';
import { codeUpper, tn } from '../i18n';
import { useTranslate } from '../i18n/react';
import { DefinitionTable } from './DefinitionTable';

interface Draft {
  name: string;
  code: string;
  description: string;
}

/**
 * Question categories.
 *
 * <p>The question count is a link rather than a number. Somebody looking at "13" wants to
 * know which thirteen, and the alternative is navigating to the question list and setting the
 * same filter by hand.</p>
 */
export function Categories() {
  const t = useTranslate();
  const [rows, setRows] = useState<QuestionCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.categories.list());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <DefinitionTable<QuestionCategory, Draft>
      crumbs={[t('app.definitions'), t('nav.categories')]}
      title={t('categories.title')}
      subtitle={t('categories.subtitle')}
      rows={rows}
      loading={loading}
      blank={{ name: '', code: '', description: '' }}
      toDraft={(row) => ({ name: row.name, code: row.code, description: row.description ?? '' })}
      fields={[
        { key: 'name', label: t('categories.name'), placeholder: t('categories.namePlaceholder') },
        // Upper-cased as it is typed: this prefix is printed on checklists and read out in stores.
        { key: 'code', label: t('categories.code'), tight: true, placeholder: 'GOR', maxLength: 6, transform: codeUpper },
        { key: 'description', label: t('categories.description') },
      ]}
      columns={[
        {
          label: t('categories.questions'),
          render: (row) =>
            row.questionCount > 0 ? (
              <Link className="nv-usage" to={`/sorular?kategori=${row.id}`}>
                {t('categories.questionCount', { count: row.questionCount })}
                <IconArrowRight size={13} />
              </Link>
            ) : (
              <span className="nv-subtle">{t('categories.empty2')}</span>
            ),
        },
      ]}
      cell={(row, key) =>
        key === 'code' ? (
          <span className="nv-code">{row.code}</span>
        ) : key === 'description' ? (
          (row.description ?? <span className="nv-subtle">{t('common.none')}</span>)
        ) : (
          row.name
        )
      }
      searchable={(row) => `${row.name} ${row.code} ${row.description ?? ''}`}
      searchPlaceholder={t('categories.searchPlaceholder')}
      describe={(row) => ({
        name: row.name,
        // The sentence that stops the click: a category taken away takes its questions out of reach.
        warning: row.questionCount > 0 ? tn('categories.inUseWarning', row.questionCount) : undefined,
      })}
      empty={t('categories.empty')}
      emptyText={t('categories.emptyText')}
      save={async (draft, id) => {
        const body = {
          name: draft.name.trim(),
          code: draft.code.trim(),
          description: draft.description.trim() || null,
        };
        if (id) await api.categories.update(id, body);
        else await api.categories.create(body);
        await reload();
      }}
      remove={async (row) => {
        await api.categories.remove(row.id);
        await reload();
      }}
    />
  );
}
